/**
 * Classic Color Blocks — Custom Element
 *
 * Responsibilities:
 *   - On mobile (≤ 749 px): intercept cell taps and open a bottom drawer
 *   - On desktop: CSS handles the hover-expand; JS sets expand direction
 *     (data-expand-x / data-expand-y) based on actual rendered position
 *   - Push interactions to the dataLayer (event: 'color_block')
 */

class ClassicColorBlocksComponent extends HTMLElement {
  connectedCallback() {
    /** @type {WeakMap<HTMLElement, () => void>} */
    this._cellHandlers = new WeakMap();
    this._mediaQuery = window.matchMedia('(max-width: 749px)');
    this._onBreakpointChange = () => this._bindCells();
    this._mediaQuery.addEventListener('change', this._onBreakpointChange);

    this._buildDrawer();
    this._bindCells();

    // CTA "ver modelos" do bloco expandido (desktop)
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const link = target.closest?.('.classic-blocks-content-button');
      const cell = link?.closest('.classic-blocks-cell');
      if (!(link instanceof HTMLAnchorElement) || !(cell instanceof HTMLElement)) return;
      this._track('cta_click', { label: cell.dataset.title || '', url: link.href, source: 'block' });
    });

    // Run after first paint so getBoundingClientRect is reliable
    requestAnimationFrame(() => this._updateExpandDirections());

    this._resizeHandler = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._updateExpandDirections(), 100);
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  disconnectedCallback() {
    if (this._mediaQuery && this._onBreakpointChange) {
      this._mediaQuery.removeEventListener('change', this._onBreakpointChange);
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
    this._backdrop?.remove();
    this._drawer?.remove();
  }

  /* ── Calculate expand direction from rendered position ──────────── */
  _updateExpandDirections() {
    const grid = this.querySelector('.classic-blocks-grid');
    if (!grid) return;

    const gridRect = grid.getBoundingClientRect();

    this.querySelectorAll('.classic-blocks-cell').forEach(cell => {
      if (!(cell instanceof HTMLElement)) return;
      const r = cell.getBoundingClientRect();

      // Expanding right would need an extra cell-width beyond the current right edge.
      // If that overflows the grid, anchor the panel to the right and grow left.
      cell.dataset.expandX = r.right + r.width > gridRect.right + 1 ? 'left' : 'right';

      // Same logic vertically.
      cell.dataset.expandY = r.bottom + r.height > gridRect.bottom + 1 ? 'up' : 'down';
    });
  }

  /* ── Build the drawer DOM (appended to <body> once) ────────────── */
  _buildDrawer() {
    // Backdrop
    this._backdrop = document.createElement('div');
    this._backdrop.className = 'classic-blocks-drawer-backdrop';
    this._backdrop.setAttribute('aria-hidden', 'true');
    this._backdrop.addEventListener('click', () => this._closeDrawer());

    // Drawer panel
    this._drawer = document.createElement('div');
    this._drawer.className = 'classic-blocks-drawer';
    this._drawer.setAttribute('role', 'dialog');
    this._drawer.setAttribute('aria-modal', 'true');
    this._drawer.setAttribute('aria-hidden', 'true');
    this._drawer.innerHTML = `
      <div class="classic-blocks-drawer-header">
        <span class="classic-blocks-drawer-handle"></span>
        <button class="classic-blocks-drawer-close-button" aria-label="Fechar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="classic-blocks-drawer-body">
        <img class="classic-blocks-drawer-image" alt="" hidden>
        <p class="classic-blocks-drawer-title"></p>
        <p class="classic-blocks-drawer-text"></p>
        <a class="classic-blocks-drawer-button" href="#"></a>
      </div>
    `;

    this._drawer
      .querySelector('.classic-blocks-drawer-close-button')
      ?.addEventListener('click', () => this._closeDrawer());

    // Prevent scroll-to-top when button has no real URL
    this._drawer
      .querySelector('.classic-blocks-drawer-button')
      ?.addEventListener('click', (event) => {
        const anchor = /** @type {HTMLAnchorElement | null} */ (event.currentTarget);
        const href = anchor?.getAttribute('href');
        if (!href || href === '#') {
          event.preventDefault();
          return;
        }
        this._track('cta_click', { label: this._drawerTitle || '', url: anchor?.href || '', source: 'drawer' });
      });

    // Swipe-down to close
    let touchStartY = 0;
    this._drawer.addEventListener('touchstart', (event) => {
      touchStartY = event.touches[0]?.clientY ?? 0;
    }, { passive: true });
    this._drawer.addEventListener('touchend', (event) => {
      const endY = event.changedTouches[0]?.clientY ?? 0;
      if (endY - touchStartY > 80) this._closeDrawer();
    }, { passive: true });

    // Tipografia da seção (classes classic-blocks--* e variáveis --ccb-*): o drawer
    // fica no <body>, fora do componente, então não herda nada dele
    this.classList.forEach((className) => {
      if (className.startsWith('classic-blocks--')) this._drawer?.classList.add(className);
    });
    Array.from(this.style).forEach((property) => {
      if (property.startsWith('--ccb-')) {
        this._drawer?.style.setProperty(property, this.style.getPropertyValue(property));
      }
    });

    document.body.appendChild(this._backdrop);
    document.body.appendChild(this._drawer);
  }

  /* ── Attach / detach click listeners on cells ───────────────────── */
  _bindCells() {
    if (!this._mediaQuery) return;
    const isMobile = this._mediaQuery.matches;

    /** @type {NodeListOf<HTMLElement>} */
    const cells = this.querySelectorAll('.classic-blocks-cell');
    cells.forEach((cell) => {
      // Always remove any existing handler first to avoid duplicates
      const existing = this._cellHandlers?.get(cell);
      if (existing) {
        cell.removeEventListener('click', existing);
        this._cellHandlers?.delete(cell);
      }

      if (isMobile) {
        const handler = () => this._openDrawer(cell);
        this._cellHandlers?.set(cell, handler);
        cell.addEventListener('click', handler);
      }
    });
  }

  /* ── Open the drawer with data from the tapped cell ────────────── */
  /** @param {HTMLElement} cell */
  _openDrawer(cell) {
    if (!this._drawer || !this._backdrop) return;
    const {
      title = '',
      text = '',
      buttonLabel = 'ver mais',
      buttonUrl = '',
      hoverImage = '',
      hoverImageAlt = '',
    } = cell.dataset;

    const titleEl = this._drawer.querySelector('.classic-blocks-drawer-title');
    const textEl  = this._drawer.querySelector('.classic-blocks-drawer-text');
    const imageEl = /** @type {HTMLImageElement | null} */ (
      this._drawer.querySelector('.classic-blocks-drawer-image')
    );
    const button  = /** @type {HTMLAnchorElement | null} */ (
      this._drawer.querySelector('.classic-blocks-drawer-button')
    );

    if (titleEl) titleEl.textContent = title;
    this._drawerTitle = title;
    if (textEl)  textEl.textContent  = text;
    if (imageEl) {
      if (hoverImage) {
        imageEl.src    = hoverImage;
        imageEl.alt    = hoverImageAlt;
        imageEl.hidden = false;
      } else {
        imageEl.removeAttribute('src');
        imageEl.alt    = '';
        imageEl.hidden = true;
      }
    }
    if (button) {
      button.textContent = buttonLabel;
      button.href        = buttonUrl || '#';
    }

    // Show backdrop and drawer
    this._backdrop.setAttribute('aria-hidden', 'false');
    this._drawer.setAttribute('aria-hidden', 'false');
    this._backdrop.classList.add('classic-blocks-drawer-backdrop--visible');
    this._drawer.classList.add('classic-blocks-drawer--open');

    // Lock scroll without losing position
    this._savedScrollY = window.scrollY;
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.classList.add('classic-blocks-scroll-locked');

    this._track('drawer_open', { label: title });
  }

  /**
   * Envia eventos pro dataLayer (GTM/GA) seguindo o mesmo padrão de
   * cart-second-frame-offer.js / newsletter-signup.js.
   * @param {string} action drawer_open | cta_click
   * @param {Record<string, unknown>} [extra]
   */
  _track(action, extra = {}) {
    try {
      // @ts-ignore - dataLayer é específico do GTM
      if (!window.dataLayer) return;
      // @ts-ignore
      window.dataLayer.push({
        event: 'color_block',
        data: { action, page: this.dataset.trackingPage || '', ...extra },
      });
    } catch {
      // Analytics não deve quebrar a experiência do usuário.
    }
  }

  /* ── Close the drawer ───────────────────────────────────────────── */
  _closeDrawer() {
    if (!this._drawer || !this._backdrop) return;
    this._backdrop.setAttribute('aria-hidden', 'true');
    this._drawer.setAttribute('aria-hidden', 'true');
    this._backdrop.classList.remove('classic-blocks-drawer-backdrop--visible');
    this._drawer.classList.remove('classic-blocks-drawer--open');

    // Restore scroll position
    document.body.classList.remove('classic-blocks-scroll-locked');
    document.body.style.top = '';
    window.scrollTo({ top: this._savedScrollY || 0, behavior: 'instant' });
  }
}

customElements.define('classic-color-blocks-component', ClassicColorBlocksComponent);
